import { hasValue } from "@arti/utilities";
import { type FC, useCallback } from "react";

import type { FlowType, TaskFilterFacets, TaskFilters } from "../../types";

import { FLOW_CATEGORY_CONFIG } from "../../utils/taskFlowGrouping";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { FilterChip } from "../common/FilterChip";
import { FormInput } from "../common/forms/FormInput";
import { Text } from "../common/Text";

interface TaskActivityFiltersProps {
  filters: TaskFilters;
  onChange: (filters: TaskFilters) => void;
  counts?: TaskFilterFacets;
}

const STATUS_OPTIONS = [
  { color: "bg-amber-500", label: "Queued", value: "queued" },
  { color: "bg-emerald-500", label: "Running", value: "running" },
  { color: "bg-emerald-500", label: "Succeeded", value: "succeeded" },
  { color: "bg-rose-500", label: "Failed", value: "failed" },
  { color: "bg-gray-400", label: "Skipped", value: "skipped" },
  { color: "bg-rose-400", label: "Canceled", value: "canceled" },
] as const;

const FLOW_TYPE_OPTIONS = [
  { label: FLOW_CATEGORY_CONFIG.plan_revision.label, value: "plan_revision" },
  { label: FLOW_CATEGORY_CONFIG.execution.label, value: "execution" },
  {
    label: FLOW_CATEGORY_CONFIG.compile_recording.label,
    value: "compile_recording",
  },
  { label: FLOW_CATEGORY_CONFIG.source_control.label, value: "source_control" },
  { label: FLOW_CATEGORY_CONFIG.analysis.label, value: "analysis" },
] satisfies { label: string; value: FlowType }[];

const FlowTypeIcon: FC<{ flowType: FlowType }> = ({ flowType }) => {
  const config = FLOW_CATEGORY_CONFIG[flowType];

  return (
    <svg
      className={`h-3 w-3 flex-shrink-0 ${config.iconClass}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d={config.iconPath}
      />
    </svg>
  );
};

const DATE_PRESETS = [
  { hours: 24, label: "24h" },
  { hours: 7 * 24, label: "7D" },
  { hours: 30 * 24, label: "30D" },
] as const;

const ClearDateRangeIcon: FC = () => (
  <svg
    className="h-3 w-3"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

export function getActiveFilterCount(filters: TaskFilters): number {
  let count = 0;
  if (filters.status && filters.status.length > 0) {
    count++;
  }
  if (filters.flowType && filters.flowType.length > 0) {
    count++;
  }
  if (filters.taskType && filters.taskType.length > 0) {
    count++;
  }
  if (filters.ticketId) {
    count++;
  }
  if (filters.dateFrom || filters.dateTo) {
    count++;
  }
  return count;
}

export const TaskActivityFiltersToggle: FC<{
  expanded: boolean;
  activeCount: number;
  onToggle: () => void;
}> = ({ expanded, activeCount, onToggle }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onToggle}
    className="px-2 py-1 text-gray-600 dark:text-gray-300"
  >
    <svg
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
      />
    </svg>
    Filters
    {activeCount > 0 && (
      <Badge
        variant="info"
        size="sm"
        className="h-4 min-w-4 border-transparent bg-emerald-600 px-1 text-white dark:bg-emerald-500 dark:text-slate-950"
      >
        {activeCount}
      </Badge>
    )}
    <svg
      className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  </Button>
);

export const TaskActivityFiltersPanel: FC<TaskActivityFiltersProps> = ({
  filters,
  onChange,
  counts,
}) => {
  const activeCount = getActiveFilterCount(filters);
  const statusCounts = counts?.statusCounts;
  const flowTypeCounts = counts?.flowTypeCounts;
  const statusCountLookup = statusCounts as Record<string, number> | undefined;
  const flowTypeCountLookup = flowTypeCounts as
    | Record<string, number>
    | undefined;
  const toggleStatus = useCallback(
    (value: string) => {
      const current = filters.status ?? [];
      const next = current.includes(value)
        ? current.filter((s) => s !== value)
        : [...current, value];
      onChange({ ...filters, status: next.length > 0 ? next : undefined });
    },
    [filters, onChange],
  );

  const toggleTaskType = useCallback(
    (value: FlowType) => {
      const current = filters.flowType ?? [];
      const next = current.includes(value)
        ? current.filter((t) => t !== value)
        : [...current, value];
      onChange({ ...filters, flowType: next.length > 0 ? next : undefined });
    },
    [filters, onChange],
  );

  const setDatePreset = useCallback(
    (hours: number) => {
      const now = new Date();
      const from = new Date(now.getTime() - hours * 60 * 60 * 1000);
      onChange({
        ...filters,
        dateFrom: from.toISOString(),
        dateTo: now.toISOString(),
      });
    },
    [filters, onChange],
  );

  const clearFilters = useCallback(() => {
    onChange({});
  }, [onChange]);

  const getStatusCount = (value: string): number | null => {
    if (!statusCounts) {
      return null;
    }
    const next = new Set([...(filters.status ?? []), value]);
    let total = 0;
    next.forEach((status) => {
      total += statusCountLookup?.[status] ?? 0;
    });
    return total;
  };

  const getFlowTypeCount = (value: FlowType): number | null => {
    if (!flowTypeCounts) {
      return null;
    }
    const next = new Set([...(filters.flowType ?? []), value]);
    let total = 0;
    next.forEach((flowType) => {
      total += flowTypeCountLookup?.[flowType] ?? 0;
    });
    return total;
  };

  return (
    <div className="space-y-3 rounded-none border border-slate-300 bg-slate-50/70 p-3 shadow-[4px_4px_0_rgba(15,23,42,0.08)] dark:border-slate-700 dark:bg-slate-950/40 dark:shadow-[4px_4px_0_rgba(15,23,42,0.24)]">
      <div>
        <Text as="label" variant="label" className="mb-1.5 block">
          Status
        </Text>
        <div className="flex flex-wrap gap-1.5">
          {STATUS_OPTIONS.map((opt) => {
            const active = filters.status?.includes(opt.value) ?? false;
            const count = getStatusCount(opt.value);
            const label =
              count === null ? opt.label : `${opt.label} (${count})`;
            return (
              <FilterChip
                key={opt.value}
                active={active}
                onClick={() => toggleStatus(opt.value)}
                icon={
                  <span className={`h-1.5 w-1.5 rounded-full ${opt.color}`} />
                }
              >
                {label}
              </FilterChip>
            );
          })}
        </div>
      </div>
      <div>
        <Text as="label" variant="label" className="mb-1.5 block">
          Flow Type
        </Text>
        <div className="flex flex-wrap gap-1.5">
          {FLOW_TYPE_OPTIONS.map((opt) => {
            const active = filters.flowType?.includes(opt.value) ?? false;
            const count = getFlowTypeCount(opt.value);
            const label =
              count === null ? opt.label : `${opt.label} (${count})`;
            return (
              <FilterChip
                key={opt.value}
                active={active}
                onClick={() => toggleTaskType(opt.value)}
                icon={<FlowTypeIcon flowType={opt.value} />}
              >
                {label}
              </FilterChip>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[140px]">
          <Text as="label" variant="label" className="mb-1.5 block">
            Ticket ID
          </Text>
          <FormInput
            type="text"
            placeholder="e.g. PROJ-123"
            value={filters.ticketId ?? ""}
            onChange={(e) => {
              const value = e.target.value.trim();
              onChange({ ...filters, ticketId: value || undefined });
            }}
          />
        </div>
        <div>
          <Text as="label" variant="label" className="mb-1.5 block">
            Date Range
          </Text>
          <div className="flex items-center gap-1">
            {DATE_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                type="button"
                onClick={() => setDatePreset(preset.hours)}
                variant="outline"
                size="sm"
                className="px-2 py-1.5 text-gray-600 dark:text-gray-400"
              >
                {preset.label}
              </Button>
            ))}
            {(hasValue(filters.dateFrom) || hasValue(filters.dateTo)) && (
              <Button
                type="button"
                onClick={() =>
                  onChange({
                    ...filters,
                    dateFrom: undefined,
                    dateTo: undefined,
                  })
                }
                variant="ghost"
                size="sm"
                className="ml-1 px-1.5 py-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Clear date range"
              >
                <ClearDateRangeIcon />
              </Button>
            )}
          </div>
        </div>
        {activeCount > 0 && (
          <Button
            type="button"
            onClick={clearFilters}
            variant="cancel"
            size="sm"
            className="dark:text-rose-400 dark:hover:bg-rose-500/10"
          >
            Reset all
          </Button>
        )}
      </div>
    </div>
  );
};
